import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, Building2, Copy, ExternalLink, Smartphone, CreditCard, Landmark, Hash } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmt = (n) => `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/invoices/${token}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white border border-slate-200 p-10 text-center max-w-md">
        <div className="text-6xl">🧾</div>
        <h1 className="font-display font-extrabold text-2xl mt-4">Factura no encontrada</h1>
        <p className="text-slate-500 mt-2">El enlace no es válido o ha sido eliminado.</p>
      </div>
    </div>
  );

  const inv = data.invoice;
  const owner = data.owner;
  const brand = owner.brand_color || "#EA580C";
  const logo = owner.logo_url ? `${BACKEND}${owner.logo_url}` : null;
  const hasPayments = owner.iban || owner.bizum_phone || owner.stripe_payment_link;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  const notifyPaid = async () => {
    try {
      await axios.post(`${API}/public/invoices/${token}/notify-paid`);
      toast.success("Hemos notificado al proveedor");
      setNotified(true);
    } catch (e) {
      toast.error("No se ha podido notificar");
    }
  };

  return (
    <div data-testid="public-invoice" className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-8">
        {/* Header */}
        <div className="bg-white border border-slate-200">
          <div className="h-2" style={{ background: brand }} />
          <div className="p-6 sm:p-8 flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {logo ? (
                <img src={logo} alt="logo" className="h-14 max-w-[140px] object-contain" />
              ) : (
                <div className="h-14 w-14 bg-slate-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-slate-400" /></div>
              )}
              <div>
                <div className="font-display font-extrabold text-xl">{owner.business_name}</div>
                <div className="text-xs text-slate-500">{owner.email} · {owner.phone}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="kbar">Factura</div>
              <div className="font-mono font-extrabold text-2xl mt-1" data-testid="invoice-number">{inv.number || "— BORRADOR —"}</div>
              {inv.status === "paid" && (
                <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 text-xs font-bold uppercase tracking-wider bg-green-100 text-green-800">
                  <CheckCircle2 className="w-3 h-3" /> Cobrada
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Hero */}
        <div className="bg-slate-900 text-white mt-px p-6 sm:p-8">
          <div className="kbar text-white/60">Importe a pagar</div>
          <div className="font-display font-extrabold text-4xl sm:text-5xl tabular-nums mt-2" data-testid="invoice-total">{fmt(inv.total)}</div>
          <div className="text-white/70 text-sm mt-1">
            Emitida el {inv.issue_date}{inv.due_date && ` · vencimiento ${inv.due_date}`}
          </div>
        </div>

        {/* Payment methods */}
        {inv.status !== "paid" && hasPayments && (
          <div className="bg-white border border-slate-200 mt-px p-6 sm:p-8 space-y-4" data-testid="payment-methods">
            <div>
              <div className="kbar">Cómo pagar</div>
              <h2 className="font-display font-extrabold text-xl mt-1">Elige el método que prefieras</h2>
              {owner.payment_note && <p className="text-sm text-slate-600 mt-2">{owner.payment_note}</p>}
            </div>

            <div className="space-y-3">
              {owner.stripe_payment_link && (
                <a href={owner.stripe_payment_link} target="_blank" rel="noreferrer" data-testid="pay-stripe"
                   className="flex items-center justify-between p-4 border border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-colors group">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-slate-900 text-white flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
                    <div>
                      <div className="font-bold">Pagar con tarjeta</div>
                      <div className="text-xs text-slate-500">Stripe · seguro y al instante</div>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-orange-600" />
                </a>
              )}
              {owner.bizum_phone && (
                <div className="flex items-center justify-between p-4 border border-slate-200" data-testid="pay-bizum">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 flex items-center justify-center text-white font-black text-xs" style={{ background: "#00B5F1" }}>BIZ</div>
                    <div>
                      <div className="font-bold">Bizum</div>
                      <div className="text-sm font-mono">{owner.bizum_phone}</div>
                    </div>
                  </div>
                  <button onClick={() => copy(owner.bizum_phone, "Bizum")} className="p-2 hover:bg-slate-100" title="Copiar">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              )}
              {owner.iban && (
                <div className="flex items-center justify-between p-4 border border-slate-200" data-testid="pay-iban">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-slate-700 text-white flex items-center justify-center"><Landmark className="w-5 h-5" /></div>
                    <div>
                      <div className="font-bold">Transferencia</div>
                      <div className="text-sm font-mono truncate max-w-[280px]">{owner.iban}</div>
                      <div className="text-[10px] text-slate-500">Indica como concepto: {inv.number || "Factura"}</div>
                    </div>
                  </div>
                  <button onClick={() => copy(owner.iban, "IBAN")} className="p-2 hover:bg-slate-100" title="Copiar">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-200">
              {notified ? (
                <div className="bg-green-50 border border-green-200 p-3 text-sm text-green-800 inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Hemos notificado a {owner.business_name} que has realizado el pago.
                </div>
              ) : (
                <button onClick={notifyPaid} data-testid="btn-notify-paid"
                  className="h-11 px-5 border-2 border-slate-200 hover:border-slate-900 font-bold text-sm inline-flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Ya he pagado — notificar al proveedor
                </button>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="bg-white border border-slate-200 mt-px p-6 sm:p-8">
          <div className="kbar mb-4">Detalle</div>
          <table className="w-full text-sm" data-testid="invoice-items">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-2">Concepto</th>
                <th className="text-right py-2 w-16">Cant.</th>
                <th className="text-right py-2 w-20">P/U</th>
                <th className="text-right py-2 w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-3">{it.concept}</td>
                  <td className="py-3 text-right tabular-nums">{it.quantity} {it.unit}</td>
                  <td className="py-3 text-right tabular-nums">{fmt(it.unit_price)}</td>
                  <td className="py-3 text-right tabular-nums font-semibold">{fmt(it.quantity * it.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 flex justify-end">
            <div className="w-full sm:w-72 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{fmt(inv.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{inv.tax_label} {inv.iva_rate}%</span><span className="tabular-nums">{fmt(inv.iva_amount)}</span></div>
              <div className="flex justify-between font-display font-extrabold text-lg pt-2 border-t border-slate-200"><span>Total</span><span className="tabular-nums">{fmt(inv.total)}</span></div>
            </div>
          </div>

          {inv.notes && (
            <div className="mt-6 p-3 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap">{inv.notes}</div>
          )}
        </div>

        {/* PDF + CSV */}
        <div className="bg-white border border-slate-200 mt-px p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            {inv.csv && (
              <div className="inline-flex items-center gap-2 text-xs text-slate-500">
                <Hash className="w-3 h-3" /> CSV Verifactu: <span className="font-mono">{inv.csv}</span>
              </div>
            )}
          </div>
          <a href={`${API}/public/invoices/${token}/pdf`} target="_blank" rel="noreferrer" data-testid="btn-pdf"
             className="h-10 px-4 border border-slate-200 hover:border-slate-300 text-sm font-bold inline-flex items-center gap-2">
            Descargar PDF
          </a>
        </div>
      </div>
    </div>
  );
}
